import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import parameterService from '../services/parameterService';

/**
 * Hook personalizado para detectar inactividad del usuario
 * Obtiene el tiempo de expiración desde la base de datos
 */
const useInactivityTimeout = () => {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const timeoutRef = useRef(null);
  const [timeoutMinutes, setTimeoutMinutes] = useState(20); // Default: 20 minutos

  // Cargar timeout desde parámetros
  useEffect(() => {
    const loadTimeout = async () => {
      try {
        const response = await parameterService.getPublic('session_timeout');
        const minutes = parseInt(response.value) || 20;
        setTimeoutMinutes(minutes);
        console.log(`✅ Timeout de sesión configurado: ${minutes} minutos (${minutes * 60} segundos)`);
      } catch (error) {
        console.error('❌ Error al cargar timeout de sesión, usando valor por defecto (20 min):', error);
        setTimeoutMinutes(20);
      }
    };

    if (isAuthenticated) {
      loadTimeout();
    }
  }, [isAuthenticated]);

  const resetTimer = useRef(() => {});

  useEffect(() => {
    // Solo activar si el usuario está autenticado
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const TIMEOUT_DURATION = timeoutMinutes * 60 * 1000; // Convertir minutos a milisegundos
    
    console.log(`⏱️ Timer configurado para: ${timeoutMinutes} minutos (${TIMEOUT_DURATION}ms)`);

    resetTimer.current = () => {
      // Limpiar el timeout anterior
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Crear nuevo timeout
      timeoutRef.current = setTimeout(() => {
        console.log('🔴 Sesión expirada por inactividad');
        logout();
        navigate('/login', { 
          state: { message: 'Tu sesión ha expirado por inactividad. Por favor, inicia sesión nuevamente.' } 
        });
      }, TIMEOUT_DURATION);
      
      // Log solo cada 5 minutos para no saturar consola
      if (Math.random() < 0.01) {
        console.log(`⏳ Timer reiniciado. Expira en ${timeoutMinutes} minutos`);
      }
    };

    // Eventos que reinician el timer
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Agregar listeners para todos los eventos
    events.forEach(event => {
      document.addEventListener(event, resetTimer.current, { passive: true });
    });

    // Iniciar el timer
    resetTimer.current();

    // Cleanup: remover listeners y limpiar timeout
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer.current);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, timeoutMinutes, logout, navigate]);

  return null;
};

export default useInactivityTimeout;
